import { consume } from '@lit/context';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { ColorOptionDto2 } from '@/api-sdk';
import { ConfiguratorContextType, configuratorGlobal } from '@/context';
import { baseStyles } from '@/styles';
import { getParamsObjectFromQueryString } from '@/utils';

import '../../../fg-checkbox';

@customElement('fg-color-option')
export class FgColorOption extends LitElement {
  @property({ type: Object })
  option?: ColorOptionDto2;

  @property({ type: Boolean })
  isMultiSelectMode = false;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  firstUpdated() {
    if (!this.option) {
      return;
    }
    let { colors } = getParamsObjectFromQueryString(this.option.queryString);
    colors = colors as string[];

    const { color } = this.configuratorContext;

    if (colors && color && colors.includes(color)) {
      this.selectOption(this.option.queryString);

      if (this.option) {
        this.selectedColorImage(true, this.option);
      }

      this.dispatchEvent(
        new Event('default-color-selected', { composed: true }),
      );
      return;
    }

    this.selectedColorImage(false);
  }

  render() {
    if (!this.option) {
      return null;
    }

    const { color, thumbnailImage, selected, queryString } = this.option;

    return html`
      <button
        class=${`
          option
          ${!this.isMultiSelectMode ? 'single' : ''}
          ${selected ? 'selected' : ''}
        `}
        @click=${() => {
          this.selectOption(queryString);
          if (this.option) {
            this.selectedColorImage(true, this.option);
          }
        }}
        data-value="${color}"
      >
        <div class="option-image">
          ${thumbnailImage
            ? html`
                <img src=${thumbnailImage} alt="" />
              `
            : html`
                <span>${color}</span>
              `}
          ${this.isMultiSelectMode
            ? html`
                <div class="option-checkbox">
                  <fg-checkbox .checked=${selected}></fg-checkbox>
                </div>
              `
            : nothing}
        </div>

        ${thumbnailImage &&
        html`
          <span class="option-label">${color}</span>
        `}
      </button>
    `;
  }

  selectOption(queryString: ColorOptionDto2['queryString']) {
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: {
          option: {
            queryString,
          },
        },
      }),
    );
  }

  selectedColorImage(colorExists: boolean, options?: ColorOptionDto2) {
    let imageObject: {
      thumbImage: string | boolean | undefined | null;
      image: string | boolean;
      color: string | boolean;
    } = {
      thumbImage: false,
      image: false,
      color: false,
    };

    if (colorExists && options) {
      const { color, mainImage, thumbnailImage } = options;

      this.dispatchEvent(
        new CustomEvent('color-selected-event', {
          detail: { color },
          composed: true,
        }),
      );

      if (!mainImage) {
        return;
      }

      imageObject = {
        thumbImage: thumbnailImage,
        image: mainImage,
        color: color,
      };
    }

    this.dispatchEvent(
      new CustomEvent('color-selected', {
        composed: true,
        detail: imageObject,
      }),
    );
  }

  static styles = [
    baseStyles,
    css`
      .option {
        font-size: 0.75rem;
        text-align: center;
        width: 100%;
        position: relative;
      }

      .option.selected {
        font-weight: 700;
      }

      .option.single.selected {
        pointer-events: none;
      }

      .option-image {
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        aspect-ratio: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        overflow: hidden;
        position: relative;
      }

      .option-image img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      .option-image span {
        padding: 0 10px;
      }

      .option-checkbox {
        color: var(--fg-gray--500, var(--fg-fallback-gray--500));
        width: 20px;
        height: 20px;
        position: absolute;
        bottom: 4px;
        left: 4px;
      }

      fg-checkbox {
        width: 20px;
        height: 20px;
      }

      .option-label {
        display: block;
        margin-top: 5px;
      }

      .option.selected .option-image {
        background: var(
          --fg-config-selected--light,
          var(--fg-fallback-config-selected--light)
        );
        border: 2px solid
          var(--fg-config-selected, var(--fg-fallback-config-selected));
      }

      .option.selected .option-checkbox {
        color: var(--fg-cta, var(--fg-fallback-cta));
      }

      .option:not(.selected):hover {
        color: var(--fg-primary, var(--fg-fallback-primary));
      }

      .option:not(.selected):hover .option-image {
        border-color: var(--fg-primary, var(--fg-fallback-primary));
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
      }
    `,
  ];
}
